import React from "react";
import { Shield, Lock, Eye, Database } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function Privacy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Lo-Fee.com</title>
        <meta
          name="description"
          content="Our privacy policy explains how Lo-Fee.com collects, uses, and protects your information when using our Bitcoin exchange fee comparison tool."
        />
        <link rel="canonical" href="https://lo-fee.com/privacy" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-8">
          <header className="space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">
              Privacy Policy
            </h1>
            <p className="text-lg text-[--text-secondary]">
              Your privacy is important to us. This policy explains how we
              collect, use, and protect your information when you use
              Lo-Fee.com.
            </p>
          </header>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              {
                icon: <Shield className="h-6 w-6 min-w-6 text-blue-500" />,
                title: "No Personal Data Collection",
                description:
                  "We do not collect or store any personal information from our users.",
              },
              {
                icon: <Lock className="h-6 w-6 min-w-6 text-green-500" />,
                title: "Secure by Design",
                description:
                  "All calculations are performed client-side in your browser.",
              },
              {
                icon: <Eye className="h-6 w-6 min-w-6 text-purple-500" />,
                title: "Transparent Operation",
                description:
                  "Our code is open for inspection, with no hidden tracking.",
              },
              {
                icon: <Database className="h-6 w-6 min-w-6 text-orange-500" />,
                title: "Minimal Data Usage",
                description:
                  "We only store essential cookies for site functionality.",
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="p-6 bg-[--bg-secondary] border-2 border-[--border-color]"
              >
                <div className="flex gap-4">
                  {feature.icon}
                  <div>
                    <h3 className="font-bold mb-2">{feature.title}</h3>
                    <p className="text-[--text-secondary]">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <section className="space-y-6">
            <h2 className="text-2xl font-bold">Information We Collect</h2>
            <div className="prose prose-lg max-w-none text-[--text-secondary]">
              <p>
                Lo-Fee.com is designed to be privacy-focused. We collect only
                the minimum information necessary to provide our service:
              </p>
              <ul>
                <li>
                  <strong>Usage Data:</strong> Anonymous analytics about page
                  views and feature usage
                </li>
                <li>
                  <strong>Technical Data:</strong> Browser type and basic device
                  information for optimization
                </li>
                <li>
                  <strong>Cookies:</strong> Essential cookies for site
                  functionality
                </li>
              </ul>
              <p>We do not collect or store:</p>
              <ul>
                <li>Personal identification information</li>
                <li>Financial information</li>
                <li>Transaction data</li>
                <li>Cryptocurrency wallet addresses</li>
              </ul>
            </div>
          </section>

          <section className="space-y-6">
            <h2 className="text-2xl font-bold">How We Use Information</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-bold">Purposes</h3>
                <ul className="space-y-2 text-[--text-secondary]">
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Improve user
                    experience
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Optimize site
                    performance
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Analyze feature
                    usage
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> Prevent abuse
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="text-lg font-bold">We Never</h3>
                <ul className="space-y-2 text-[--text-secondary]">
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">×</span> Sell user data
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">×</span> Share with third
                    parties
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">×</span> Track individual
                    users
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">×</span> Use for marketing
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <h2 className="text-2xl font-bold">Your Rights</h2>
            <div className="prose prose-lg max-w-none text-[--text-secondary]">
              <p>
                Under privacy laws like GDPR, you have certain rights regarding
                your data:
              </p>
              <ul>
                <li>Right to access any data we hold about you</li>
                <li>Right to request deletion of your data</li>
                <li>Right to object to data processing</li>
                <li>Right to data portability</li>
              </ul>
              <p>
                Since we collect minimal data, most of these rights may not
                apply. However, we're committed to honoring any valid requests.
              </p>
            </div>
          </section>

          <section className="bg-[--bg-secondary] border-2 border-[--border-color] p-6">
            <h2 className="text-xl font-bold mb-4">Contact Us</h2>
            <p className="text-[--text-secondary]">
              If you have any questions about our privacy policy or how we
              handle your data, please contact us at{" "}
              <a
                href="mailto:contact@lo-fee.com"
                className="text-[--accent] hover:underline"
              >
                contact@lo-fee.com
              </a>
            </p>
            <p className="text-sm text-[--text-secondary] mt-4">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </section>
        </div>
      </div>
    </>
  );
}
